import {FC, ReactNode, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Add} from '@mui/icons-material';
import {Box, IconButton, Typography} from '@mui/material';

import {Editable} from './Editable';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {formatStartEndWithDuration} from '../../../../../utils/dateUtils/calculateDuration';
import {sortByYearMonthDesc} from '../../../../../utils/dateUtils/sortEvents';
import * as t from '../../types';

type DateRange = {
  month: number;
  year: number;
};

interface WorkExperienceProps {
  data: {
    workExperience: t.ProfileWorkExperience[];
  };
  onEdit?: (formType: t.ProfileFormType, id?: string) => void;
}

type ExperienceByOrganization = {
  organizationName: string;
  resourceId: string;
  roles:
    | {
        type: 'single';
        data: t.ProfileWorkExperience & {
          duration?: string | null;
        };
      }
    | {
        type: 'multiple';
        data: {
          duration?: string | null;
          items: (t.ProfileWorkExperience & {
            duration: string | null;
          })[];
        };
      };
};

const createExperienceDateRange = (experience: t.ProfileWorkExperience) => ({
  start:
    experience.startMonth && experience.startYear ? {month: experience.startMonth, year: experience.startYear} : null,
  end: experience.endMonth && experience.endYear ? {month: experience.endMonth, year: experience.endYear} : undefined,
});

const createExperienceItem = (
  experience: t.ProfileWorkExperience,
  dateRange: {start?: DateRange | null; end?: DateRange},
  locale: string
) => ({
  ...experience,
  duration: !dateRange.start
    ? null
    : formatStartEndWithDuration(locale, dateRange.start, dateRange.end, {datesOnly: true}),
});

const Experience: FC<{
  header: ReactNode;
  body: ReactNode;
}> = (props) => {
  return (
    <>
      <Box>{props.header}</Box>
      <Box>{props.body}</Box>
    </>
  );
};

const WorkExperience: FC<WorkExperienceProps> = ({onEdit, data}) => {
  const {t} = useTranslation();
  const lang = useContext(LanguageContext);
  const sortedExperiences = useMemo(
    () => sortByYearMonthDesc<t.ProfileWorkExperience>(data.workExperience),
    [data.workExperience]
  );
  const experienceList: ExperienceByOrganization[] = useMemo(() => {
    const groupedExperiences: ExperienceByOrganization[] = [];

    for (let i = 0; i < sortedExperiences.length; i++) {
      const current = sortedExperiences[i];
      const dateRange = createExperienceDateRange(current);
      const isSameOrganization = (org1Name: string, org2Name: string) => org1Name === org2Name;

      if (
        i < sortedExperiences.length - 1 &&
        isSameOrganization(current.organizationName, sortedExperiences[i + 1].organizationName)
      ) {
        const multipleEntry = {
          organizationName: current.organizationName,
          resourceId: current.id,
          roles: {
            type: 'multiple' as const,
            data: {
              duration: null, // NOTE: will be set outside of this loop
              items: [createExperienceItem(current, dateRange, lang)],
            },
          },
        };

        while (
          i < sortedExperiences.length - 1 &&
          isSameOrganization(current.organizationName, sortedExperiences[i + 1].organizationName)
        ) {
          i++;
          const nextExperience = sortedExperiences[i];
          const nextDateRange = createExperienceDateRange(nextExperience);
          multipleEntry.roles.data.items.push(createExperienceItem(nextExperience, nextDateRange, lang));
        }

        const timeRange = {
          start: createExperienceDateRange(multipleEntry.roles.data.items[multipleEntry.roles.data.items.length - 1])
            .start,
          end: createExperienceDateRange(multipleEntry.roles.data.items[0]).end,
        };

        multipleEntry.roles.data.duration = timeRange.start
          ? formatStartEndWithDuration(lang, timeRange.start, timeRange.end, {durationOnly: true})
          : (null as any);

        groupedExperiences.push(multipleEntry as any);
      } else {
        groupedExperiences.push({
          organizationName: current.organizationName,
          resourceId: current.id,
          roles: {
            type: 'single',
            data: createExperienceItem(current, dateRange, lang),
          },
        });
      }
    }

    return groupedExperiences;
  }, [sortedExperiences, lang]);

  return (
    <Box sx={{mt: 10}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
        <Typography variant='h2'>{t('applicantProfile.workExperience.title')}</Typography>
        <IconButton
          onClick={() => onEdit?.('work-experience')}
          data-testid='add-work-experience'
          name='add'
          sx={{flexShrink: 0}}
        >
          <Add sx={{color: greenColors[100]}} />
        </IconButton>
      </Box>
      {experienceList.map((experience, idx) => (
        <Box
          key={experience.resourceId || idx}
          sx={{
            paddingBottom: 3,
            mt: 3,
            borderBottom: '1px solid',
            borderColor: idx === sortedExperiences.length - 1 ? 'transparent' : orangeColors[3],
            '&:last-child': {
              borderColor: 'transparent',
            },
          }}
        >
          {experience.roles.type === 'single' ? (
            (() => {
              const pos = experience.roles.data;
              return (
                <Experience
                  header={
                    <Editable
                      onEdit={() => {
                        onEdit?.('work-experience', pos.id);
                      }}
                      sx={{
                        mt: 0,
                      }}
                      testid='edit-work-experience'
                    >
                      <Typography variant='h3'>{pos.roleName}</Typography>
                      <Typography variant='body1'>{pos.organizationName}</Typography>
                      <Typography
                        component={'span'}
                        variant='body1'
                        color={{
                          color: greenColors[70],
                        }}
                      >
                        {pos.duration}
                      </Typography>
                    </Editable>
                  }
                  body={
                    <Box>
                      <Typography variant='body1' my={3} dangerouslySetInnerHTML={{__html: pos.description || ''}} />
                    </Box>
                  }
                />
              );
            })()
          ) : (
            <Experience
              header={
                <>
                  <Typography variant='h3'>{experience.organizationName}</Typography>
                  <Typography
                    component={'span'}
                    variant='body1'
                    color={{
                      color: greenColors[70],
                    }}
                  >
                    {experience.roles.data.duration}
                  </Typography>
                </>
              }
              body={(() => {
                const rolesList = experience.roles.data.items;
                return (
                  <>
                    {rolesList.map((role, idx) => (
                      <Editable
                        key={role.id}
                        onEdit={() => {
                          onEdit?.('work-experience', role.id);
                        }}
                        sx={{
                          mt: 4,
                        }}
                        testid='edit-work-experience'
                      >
                        <Box display={'flex'}>
                          <Box
                            sx={{
                              flex: '0 0 64px',
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                margin: '0 auto',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '24px',
                                  flex: '0 0 24px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginBottom: 1,
                                }}
                              >
                                <Box width={16} height={16} sx={{backgroundColor: greenColors[600], borderRadius: 8}} />
                              </Box>
                              <Box
                                sx={{
                                  width: '2px',
                                  margin: '0 auto',
                                  flex: 1,
                                  marginBottom: -6,
                                  backgroundColor: greenColors[600],
                                  display: idx === rolesList.length - 1 ? 'none' : 'block',
                                }}
                              />
                            </Box>
                          </Box>
                          <Box ml={4}>
                            <Typography variant='h4'>{role.roleName}</Typography>
                            {role.duration ? (
                              <Typography variant='body1' color='primary.var70'>
                                {role.duration}
                              </Typography>
                            ) : null}
                            <Typography variant='body1' my={3}>
                              {role.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Editable>
                    ))}
                  </>
                );
              })()}
            />
          )}
        </Box>
      ))}
    </Box>
  );
};

export default WorkExperience;
